import type { FormEvent, ReactNode } from "react"
import { ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react"

import { QuanticaLogo } from "@/components/brand/QuanticaLogo"
import { QuantumPlusBadge } from "@/components/brand/QuantumPlusBadge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Field, Notice } from "@/pages/client-area/auth-forms"
import type { Status } from "@/pages/client-area/auth-helpers"

/**
 * Marcação do cartão de login, compartilhada pela página interativa
 * (ClientLoginPage) e pelo app shell estático (LoginShell). Como as duas usam
 * exatamente este JSX, o HTML gerado no build é igual ao primeiro render do
 * React e o shell pode ser hidratado em vez de substituído.
 */

export function LoginFrame({ children }: { children: ReactNode }) {
  return (
    <main className="grid min-h-screen place-items-center overflow-x-hidden bg-[#f7f4fb] px-4 py-10 text-[#201936]">
      <section className="w-full max-w-md min-w-0 rounded-lg border border-[#ece6f3] bg-white p-6 shadow-[0_24px_80px_rgba(32,25,54,0.12)] sm:p-8">
        <div className="mb-7 flex flex-col items-center text-center">
          <QuanticaLogo markClassName="h-12 w-12" wordmarkClassName="hidden" />
          <QuantumPlusBadge variant="accent" className="mt-4" />
          <h1 className="mt-2 text-2xl font-semibold tracking-tight">Entrar no app</h1>
        </div>
        {children}
      </section>
    </main>
  )
}

export type LoginFormProps = {
  email: string
  password: string
  showPassword: boolean
  status: Status
  message: string
  onEmailChange: (value: string) => void
  onPasswordChange: (value: string) => void
  onToggleShowPassword: () => void
  onForgotPassword: () => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

export function LoginForm({
  email,
  password,
  showPassword,
  status,
  message,
  onEmailChange,
  onPasswordChange,
  onToggleShowPassword,
  onForgotPassword,
  onSubmit,
}: LoginFormProps) {
  return (
    <form onSubmit={onSubmit} className="grid min-w-0 gap-4">
      <Field label="E-mail">
        <Input
          required
          type="email"
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          className="h-11 max-w-full"
          autoComplete="email"
        />
      </Field>
      <Field label="Senha">
        <div className="relative min-w-0">
          <Input
            required
            minLength={8}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
            className="h-11 max-w-full pr-11"
            autoComplete="current-password"
          />
          <button
            type="button"
            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            aria-pressed={showPassword}
            onClick={onToggleShowPassword}
            className="absolute right-2 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-md text-[#6b6080] transition hover:bg-[#f3eef8] hover:text-[#201936] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e82b77]/30"
          >
            {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        </div>
      </Field>
      <button
        type="button"
        onClick={onForgotPassword}
        className="w-fit text-sm font-medium text-[#433475] underline-offset-4 transition hover:text-[#e82b77] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e82b77]/30"
      >
        Esqueci minha senha
      </button>
      <Notice status={status} message={message} />
      <Button
        type="submit"
        disabled={status === "saving"}
        className="h-11 max-w-full bg-[#cf1f66] text-white hover:bg-[#b5185a]"
      >
        {status === "saving" ? <Loader2 className="animate-spin" /> : <ArrowRight />}
        Entrar
      </Button>
    </form>
  )
}
